import React, { useState, useEffect } from 'react';
import './ChemicalDisclosureTable.css';
import IngredientsService from '../services/IngredientsService';
import { toast } from 'react-toastify';

const ChemicalDisclosureTable = ({ ingredients, setIngredients, wellData, totalClean }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredIngredients, setFilteredIngredients] = useState([]);
  const [ingredientsList, setIngredientsList] = useState([]);
  const [isLoading] = useState(false);
  const [totalPercentage, setTotalPercentage] = useState(0);
  const [vendorCompanyMap, setVendorCompanyMap] = useState({});

  // Load ingredients list for autocomplete
  useEffect(() => {
    const loadIngredientsList = async () => {
      try {
        const list = await IngredientsService.getIngredientsList();
        setIngredientsList(list);
      } catch (error) {
        console.error('Error loading ingredients list:', error);
        toast.error('Failed to load ingredients list');
      }
    };

    loadIngredientsList();
  }, []);

  // Load vendors and create a mapping of vendor IDs to company names
  useEffect(() => {
    const loadVendors = async () => {
      try {
        const vendors = await IngredientsService.getVendors();
        const vendorMap = {};
        
        vendors.forEach(vendor => {
          vendorMap[vendor.id] = vendor.company;
        });
        
        setVendorCompanyMap(vendorMap);
      } catch (error) {
        console.error('Error loading vendors:', error);
      }
    };

    loadVendors();
  }, []);

  // Update filtered ingredients when ingredients or search term changes
  useEffect(() => {
    if (!ingredients || !Array.isArray(ingredients)) {
      setFilteredIngredients([]);
      setTotalPercentage(0);
      return;
    }

    try {
      // If no search term, show all ingredients
      if (!searchTerm) {
        setFilteredIngredients(ingredients);
      } else {
        const searchLower = searchTerm.toLowerCase();
        
        // First, find all unique values that match the search term
        const matchingTradeNames = new Set();
        const matchingSuppliers = new Set();
        const matchingPurposes = new Set();
        const matchingIngredientNames = new Set();
        const matchingCasNumbers = new Set();
        const matchingCodes = new Set();
        
        // Collect all matching values
        ingredients.forEach(ingredient => {
          if ((ingredient.tradeName || '').toLowerCase().includes(searchLower)) {
            matchingTradeNames.add((ingredient.tradeName || '').toLowerCase());
          }
          if ((ingredient.supplier || '').toLowerCase().includes(searchLower)) {
            matchingSuppliers.add((ingredient.supplier || '').toLowerCase());
          }
          if ((ingredient.purpose || '').toLowerCase().includes(searchLower)) {
            matchingPurposes.add((ingredient.purpose || '').toLowerCase());
          }
          if ((ingredient.ingredientName || '').toLowerCase().includes(searchLower)) {
            matchingIngredientNames.add((ingredient.ingredientName || '').toLowerCase());
          }
          if ((ingredient.casNumber || '').toLowerCase().includes(searchLower)) {
            matchingCasNumbers.add((ingredient.casNumber || '').toLowerCase());
          }
          if ((ingredient.code || '').toLowerCase().includes(searchLower)) {
            matchingCodes.add((ingredient.code || '').toLowerCase());
          }
        });
        
        // Then filter ingredients to include all that match any of the collected values
        const filtered = ingredients.filter(ingredient => {
          return (
            matchingTradeNames.has((ingredient.tradeName || '').toLowerCase()) ||
            matchingSuppliers.has((ingredient.supplier || '').toLowerCase()) ||
            matchingPurposes.has((ingredient.purpose || '').toLowerCase()) ||
            matchingIngredientNames.has((ingredient.ingredientName || '').toLowerCase()) ||
            matchingCasNumbers.has((ingredient.casNumber || '').toLowerCase()) ||
            matchingCodes.has((ingredient.code || '').toLowerCase()) ||
            // Also include direct matches to handle partial matches
            (ingredient.tradeName || '').toLowerCase().includes(searchLower) ||
            (ingredient.supplier || '').toLowerCase().includes(searchLower) ||
            (ingredient.purpose || '').toLowerCase().includes(searchLower) ||
            (ingredient.ingredientName || '').toLowerCase().includes(searchLower) ||
            (ingredient.casNumber || '').toLowerCase().includes(searchLower) ||
            (ingredient.code || '').toLowerCase().includes(searchLower)
          );
        });
        
        setFilteredIngredients(filtered);
        console.log(filtered);
      }

      // Calculate total percentage based on the ratio of individual ingredient masses to the total mass
      const totalMass = calculateTotalMassInternal(ingredients);
      let total = 0;
      
      if (totalMass > 0) {
        // Recalculate percentInHFJob for each ingredient based on the new total mass calculation
        ingredients.forEach(ingredient => {
          const mass = parseFloat(ingredient.massIngredient) || 0;
          ingredient.percentInHFJob = mass / totalMass;
          total += ingredient.percentInHFJob;
        });
      }
      
      setTotalPercentage(total);
    } catch (error) {
      console.error('Error filtering ingredients:', error);
      setFilteredIngredients([]);
    }
  }, [ingredients, searchTerm]);

  // Add a new ingredient
  const handleAddIngredient = () => {
    const newIngredient = {
      tradeName: '',
      supplier: wellData?.customer || '',
      vendorId: null, // Initialize with null vendor ID
      purpose: 'Fracturing',
      ingredientName: '',
      casNumber: '',
      percentInAdditive: 1, // Stored as fraction (0-1), 1 = 100%
      percentInHFJob: 0,
      massIngredient: 0,
      units: 'gal',
      comments: '',
      isNonMsds: false,
      isCustomAdded: true, // Mark as custom added ingredient
      code: `CUSTOM-${Date.now()}`
    };

    setIngredients([...ingredients, newIngredient]);
  };


  // Remove an ingredient
  const handleRemoveIngredient = (index) => {
    const updatedIngredients = [...ingredients];
    updatedIngredients.splice(index, 1);
    setIngredients(updatedIngredients);
  };


  // Update ingredient field
  const handleIngredientChange = (index, field, value) => {
    const updatedIngredients = [...ingredients];
    const ingredient = updatedIngredients[index];
    
    // Update the field
    ingredient[field] = value;
    
    // If updating supplier field, try to find matching vendor ID
    if (field === 'supplier') {
      // Find vendor ID by company name
      const vendorId = Object.keys(vendorCompanyMap).find(
        id => vendorCompanyMap[id].toLowerCase() === value.toLowerCase()
      );
      
      if (vendorId) {
        ingredient.vendorId = parseInt(vendorId);
      } else {
        ingredient.vendorId = null;
      }
    }
    
    // If updating casNumber field to "Proprietary", ensure there's a supplier/vendor
    if (field === 'casNumber' && (value.toLowerCase().includes('proprietary') || value.toLowerCase() === 'n/a')) {
      // If no supplier is set, use the well's customer as default supplier
      if (!ingredient.supplier && wellData?.customer) {
        ingredient.supplier = wellData.customer;
        
        // Try to find matching vendor ID for the customer
        const vendorId = Object.keys(vendorCompanyMap).find(
          id => vendorCompanyMap[id].toLowerCase() === wellData.customer.toLowerCase()
        );
        
        if (vendorId) {
          ingredient.vendorId = parseInt(vendorId);
        }
      }
    }
    
    // Calculate total mass using the same method as calculateTotalMass
    const totalMass = calculateTotalMassInternal(ingredients);
    
    // Special handling for certain fields
    if (field === 'ingredientName') {
      // Try to find matching ingredient in the list
      const matchedIngredient = ingredientsList.find(
        item => item.name.toLowerCase() === value.toLowerCase()
      );
      
      if (matchedIngredient) {
        ingredient.casNumber = matchedIngredient.casNumber || ingredient.casNumber;
      }
    } else if (field === 'massIngredient') {
      // Update mass and recalculate percentInHFJob
      const mass = parseFloat(value) || 0;
      ingredient.massIngredient = mass;
      
      // Calculate percentInHFJob based on mass ratio
      if (totalMass > 0) {
        // Update all ingredients' percentInHFJob based on their mass
        updatedIngredients.forEach(ing => {
          ing.percentInHFJob = parseFloat(ing.massIngredient) / totalMass;
        });
      }
    } else if (field === 'percentInHFJob') {
      // Update percentInHFJob and recalculate massIngredient
      const percent = parseFloat(value) || 0;
      ingredient.percentInHFJob = percent;
      
      // If we have specificGravity, use the formula: volume * 8.345 * specific_gravity
      if (ingredient.specificGravity) {
        const volume = totalClean * percent;
        ingredient.massIngredient = volume * 8.345 * ingredient.specificGravity;
      } else {
        // Fallback if no specificGravity is available
        ingredient.massIngredient = totalMass * percent;
      }
    } else if (field === 'specificGravity') {
      // Update specificGravity and recalculate massIngredient
      const specificGravity = parseFloat(value) || 1.0;
      ingredient.specificGravity = specificGravity;
      
      // Recalculate mass based on volume and specific gravity
      if (ingredient.volume) {
        ingredient.massIngredient = ingredient.volume * 8.345 * specificGravity;
        
        // Update percentInHFJob based on new mass
        if (totalMass > 0) {
          ingredient.percentInHFJob = ingredient.massIngredient / totalMass;
        }
      }
    } else if (field === 'volume') {
      // Update volume and recalculate massIngredient
      const volume = parseFloat(value) || 0;
      ingredient.volume = volume;
      
      // Recalculate mass based on volume and specific gravity
      const specificGravity = parseFloat(ingredient.specificGravity) || 1.0;
      const unitsLower = (ingredient.units || '').toLowerCase().trim();
      if (unitsLower === 'lb' || unitsLower === 'lbs') {
        // If units are already in pounds, don't do the conversion
        // percentInAdditive is stored as fraction (0-1), use directly
        ingredient.massIngredient = volume * ingredient.percentInAdditive;
      } else {
        // For gallons or any other unit, convert to pounds
        // percentInAdditive is stored as fraction (0-1), use directly
        ingredient.massIngredient = volume * specificGravity * 8.345 * ingredient.percentInAdditive;
      }
      
      // Update percentInHFJob based on new mass
      if (totalMass > 0) {
        // Recalculate total mass with the new volume
        const newTotalMass = calculateTotalMassInternal(updatedIngredients);
        updatedIngredients.forEach(ing => {
          ing.percentInHFJob = parseFloat(ing.massIngredient) / newTotalMass;
        });
      }
    } else if (field === 'percentInAdditive') {
      // Update percentInAdditive and recalculate massIngredient if this is a component
      const percentInAdditive = parseFloat(value) || 0;
      ingredient.percentInAdditive = percentInAdditive;
      
      // If this is a component of a chemical (part of a tradeName group)
      // Find the main chemical with the same tradeName (percentInAdditive = 1 means 100%)
      const mainChemical = ingredients.find(ing => 
        ing.tradeName === ingredient.tradeName && 
        ing.percentInAdditive === 1
      );
      
      if (mainChemical) {
        // Calculate this component's mass based on its percentage of the main chemical's mass
        // percentInAdditive is now stored as fraction (0-1), use directly
        ingredient.massIngredient = mainChemical.massIngredient * percentInAdditive;
        
        // Update percentInHFJob based on new mass
        if (totalMass > 0) {
          ingredient.percentInHFJob = ingredient.massIngredient / totalMass;
        }
      }
    }
    
    setIngredients(updatedIngredients);
  };

// Format percentage for display
const formatPercentage = (value) => {
  if (value === undefined || value === null) return '0.00%';
  // Multiply by 100 to convert decimal to percentage
  return `${(parseFloat(value) * 100).toFixed(2)}%`;
};

// Internal function to calculate total mass based on unique tradeNames
// This is used both for the display and for internal calculations
const calculateTotalMassInternal = (ingredientsList) => {
  if (!ingredientsList || !Array.isArray(ingredientsList) || ingredientsList.length === 0) {
    return 0;
  }

  // Create a map to store unique tradeName data
  const tradeNameMap = new Map();
  
  // Process each ingredient to find unique tradeNames and their properties
  ingredientsList.forEach(ingredient => {
    const tradeName = ingredient.tradeName || '';
    if (!tradeName) return; // Skip ingredients without a tradeName
    
    // If this tradeName is not in our map yet, add it
    if (!tradeNameMap.has(tradeName)) {
      // Find the main ingredient (with percentInAdditive = 1 (100%) or the first one)
      const mainIngredient = ingredientsList.find(ing => 
        ing.tradeName === tradeName && ing.percentInAdditive === 1
      ) || ingredientsList.find(ing => ing.tradeName === tradeName);
      
      if (mainIngredient) {
        tradeNameMap.set(tradeName, {
          volume: parseFloat(mainIngredient.volume) || 0,
          specificGravity: parseFloat(mainIngredient.specificGravity) || 1,
          units: mainIngredient.units || 'gal'
        });
      }
    }
  });
  
  // If no valid tradeNames were found, fall back to the old calculation method
  if (tradeNameMap.size === 0) {
    return ingredientsList.reduce((sum, ing) => sum + (parseFloat(ing.massIngredient) || 0), 0);
  }
  
  // Calculate total mass from the unique tradeNames
  let totalMass = 0;
  
  tradeNameMap.forEach((data, tradeName) => {
    const unitsLower = (data.units || '').toLowerCase().trim();
    if (unitsLower === 'gal') {
      // For gallons: volume * specificGravity * 8.345
      const mass = data.volume * data.specificGravity * 8.345;
      totalMass += mass;
    } else if (unitsLower === 'lb' || unitsLower === 'lbs') {
      // For pounds: use massIngredient directly (volume field now contains back-calculated gallons)
      const firstIngredient = ingredientsList.find(ing => ing.tradeName === tradeName);
      if (firstIngredient) {
        totalMass += parseFloat(firstIngredient.massIngredient) || 0;
      }
    } else {
      // Default case: try to use massIngredient from the first ingredient with this tradeName
      const firstIngredient = ingredientsList.find(ing => ing.tradeName === tradeName);
      if (firstIngredient) {
        totalMass += parseFloat(firstIngredient.massIngredient) || 0;
      }
    }
  });
  
  return totalMass;
};

// Calculate total mass for display in the table footer
const calculateTotalMass = (ingredients) => {
  return Math.round(calculateTotalMassInternal(ingredients));
};

  // Check if total percentage is valid
  const isPercentageValid = totalPercentage >= 0.97 && totalPercentage <= 1.03;

  return (
    <div className="ingredients-table">
      <div className="ingredients-controls">
        <div>
          <button 
            className="add-ingredient-button" 
            onClick={handleAddIngredient}
            disabled={isLoading}
          >
            Add Ingredient
          </button>
        </div>
        <div className="total-percentage-display">
          Total Percentage: 
          <span className={isPercentageValid ? '' : 'error'}>
            {' '}{formatPercentage(totalPercentage)}
          </span>
          {!isPercentageValid && (
            <span className="percentage-warning">
              (Should be between 97% and 103%)
            </span>
          )}
        </div>
      </div>

      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search ingredients..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="ingredients-table-container">
        <table>
          <thead>
            <tr>
              <th>Trade Name</th>
              <th>Supplier</th>
              <th>Purpose</th>
              <th>Ingredient Name</th>
              <th>CAS Number</th>
              <th>Volume (gal)</th>
              <th>Specific Gravity</th>
              <th>Additive Fraction</th>
              <th>% of HF Job</th>
              <th>Mass (lbs)</th>
              <th>Comments</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredIngredients.map((ingredient, index) => {
              // Use a more reliable way to find the original index
              const originalIndex = ingredients.findIndex(ing => 
                ing.code === ingredient.code && 
                ing.ingredientName === ingredient.ingredientName
              );
              
              // If we can't find the original index, use the current index as fallback
              const safeIndex = originalIndex >= 0 ? originalIndex : index;
              
              return (
                <tr 
                  key={`${ingredient.code || 'unknown'}-${index}`}
                  className={ingredient.isNonMsds ? 'non-msds-row' : ''}
                >
                  <td>
                    {!ingredient.isNonMsds && (
                      <input
                        type="text"
                        className="ingredient-input"
                        value={ingredient.tradeName || ''}
                        onChange={(e) => handleIngredientChange(safeIndex, 'tradeName', e.target.value)}
                        placeholder="Trade Name"
                      />
                    )}
                  </td>
                  <td>
                    {!ingredient.isNonMsds && (
                      <input
                        type="text"
                        className="ingredient-input"
                        value={
                          // Display company name if available, otherwise use supplier value
                          (ingredient.vendorId && vendorCompanyMap[ingredient.vendorId]) || 
                          ingredient.supplier || 
                          ''
                        }
                        onChange={(e) => handleIngredientChange(safeIndex, 'supplier', e.target.value)}
                        placeholder="Supplier"
                        list="vendors-list"
                      />
                    )}
                    <datalist id="vendors-list">
                      {Object.values(vendorCompanyMap).map((company, i) => (
                        <option key={`vendor-${i}`} value={company} />
                      ))}
                    </datalist>
                  </td>
                  <td>
                    {!ingredient.isNonMsds && (
                      <input
                        type="text"
                        className="ingredient-input"
                        value={ingredient.purpose || ''}
                        onChange={(e) => handleIngredientChange(safeIndex, 'purpose', e.target.value)}
                        placeholder="Purpose"
                      />
                    )}
                  </td>
                  <td>
                    <input
                      type="text"
                      className="ingredient-input"
                      value={ingredient.ingredientName || ''}
                      onChange={(e) => handleIngredientChange(safeIndex, 'ingredientName', e.target.value)}
                      placeholder="Ingredient Name"
                      list="ingredients-list"
                    />
                    <datalist id="ingredients-list">
                      {ingredientsList.map((item, i) => (
                        <option key={`${item.name}-${i}`} value={item.name} />
                      ))}
                    </datalist>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="ingredient-input"
                      value={ingredient.casNumber || ''}
                      onChange={(e) => handleIngredientChange(safeIndex, 'casNumber', e.target.value)}
                      placeholder="CAS Number"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className={`ingredient-input number-input ${!ingredient.isCustomAdded ? 'disabled-input' : ''}`}
                      value={parseFloat(ingredient.volume || 0).toFixed(2)}
                      disabled={!ingredient.isCustomAdded}
                      min="0"
                      step="0.01"
                      placeholder="Volume"
                      onChange={(e) => handleIngredientChange(safeIndex, 'volume', parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className={`ingredient-input number-input ${!ingredient.isCustomAdded ? 'disabled-input' : ''}`}
                      value={parseFloat(ingredient.specificGravity || 1.0).toFixed(2)}
                      disabled={!ingredient.isCustomAdded}
                      step="0.01"
                      min="0"
                      onChange={(e) => handleIngredientChange(safeIndex, 'specificGravity', parseFloat(e.target.value) || 1.0)}
                    />
                  </td>
                  <td>
                    {!ingredient.isNonMsds && (
                      <input
                        type="number"
                        className={`ingredient-input number-input ${!ingredient.isCustomAdded ? 'disabled-input' : ''}`}
                        value={ingredient.percentInAdditive || 0}
                        onChange={(e) => handleIngredientChange(
                          safeIndex, 
                          'percentInAdditive', 
                          parseFloat(e.target.value) || 0
                        )}
                        min="0"
                        max="1"
                        step="0.001"
                        title="Enter as fraction (0-1), e.g., 0.5 for 50%"
                        disabled={!ingredient.isCustomAdded}
                      />
                    )}
                  </td>
                  <td>
                    <input
                      type="number"
                      className={`ingredient-input number-input ${!ingredient.isCustomAdded ? 'disabled-input' : ''}`}
                      value={parseFloat(((ingredient.percentInHFJob || 0) * 100).toFixed(2))}
                      onChange={(e) => handleIngredientChange(
                        safeIndex, 
                        'percentInHFJob', 
                        (parseFloat(e.target.value) || 0) / 100
                      )}
                      min="0"
                      max="100"
                      step="0.01"
                      disabled={!ingredient.isCustomAdded}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className={`ingredient-input number-input ${!ingredient.isCustomAdded ? 'disabled-input' : ''}`}
                      value={Math.round(ingredient.massIngredient || 0)}
                      onChange={(e) => handleIngredientChange(
                        safeIndex, 
                        'massIngredient', 
                        parseFloat(e.target.value) || 0
                      )}
                      min="0"
                      step="1"
                      disabled={!ingredient.isCustomAdded}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="ingredient-input"
                      value={ingredient.comments || ''}
                      onChange={(e) => handleIngredientChange(safeIndex, 'comments', e.target.value)}
                      placeholder="Comments"
                      disabled={ingredient.isNonMsds}
                    />
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="remove-button"
                        onClick={() => handleRemoveIngredient(safeIndex)}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="8" className="total-label">Total:</td>
              <td className={isPercentageValid ? '' : 'error'}>
                {formatPercentage(totalPercentage)}
              </td>
              <td>
                {calculateTotalMass(ingredients)} lbs
              </td>
              <td colSpan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="ingredients-help">
        <h3>Instructions:</h3>
        <ul>
          <li>Add ingredients for chemicals with trade names, suppliers, and purposes.</li>
          <li>CAS numbers should be in the format XX-XX-X or marked as "Proprietary" or "N/A".</li>
          <li>The total percentage of all ingredients should be between 97% and 103%.</li>
          <li>Mass (lbs) is calculated as: volume (gal) × 8.345 (lbs/gal) × specific gravity.</li>
          <li>The % of HF Job for each ingredient is its Mass (lbs) divided by the total Mass (lbs).</li>
          <li>All ingredients follow the MSDS route with complete chemical disclosure information.</li>
        </ul>
      </div>
    </div>
  );
};

export default ChemicalDisclosureTable;
